<template>
  <PageTemplate>
    <Loader v-if="!isLoaded"/>
    <div v-else>
      <BackButton
          :label="$t('general.button.back')"
          :to="{ name: 'analyticsHome' }"
          marginBottom="5"/>

      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          Anwesenheitsliste monatlich
        </h6>
      </div>

      <div class="element-box">
        <h5 class="form-header mb-4">Betreutes Wohnen</h5>

        <Search
            :filters="filters"
            :loading="isSearching"
            :classificationDropdown="classificationDropdown"
            :officeDropdown="officeDropdown"
            :coachDropdown="coachDropdown"
            :statuDropdown='statusDropdown'
            :unemployedDropdown="unemployedDropdown"
            :statusDropdown="unemployedStatusFiltered"
            @search="getCoachingList"
            @dropdown-fetch="handleDropdownSearch"
            @clear-dropdown="handleClearDropdown"/>

        <div class="row align-items-center justify-content-between">
          <div class="mt-3">
            <PerPage :values="[10, 25, 50, 100]" @update:perPage="getCoachingList"/>
          </div>
        </div>

        <div class="table-responsive overflow-auto">
          <table class="table table-striped">
            <thead>
            <tr>
              <th>Status</th>
              <th>Vorname</th>
              <th>Nachname</th>
              <th>Standort</th>
              <th>Start</th>
              <th>Ende</th>
              <th class="text-end">Stand</th>
              <th class="text-end">Stand %</th>
              <th class="text-end">Einheiten</th>
              <th class="text-center">Anwesenheitsliste</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(coaching) in coachingList?.data" :key="coaching.id">
              <td>{{ $t(`coaching_status.${coaching?.status}`) }}</td>
              <td>{{ coaching?.firstName }}</td>
              <td>{{ coaching?.lastName }}</td>
              <td>{{ coaching?.location }}</td>
              <td>{{ coaching?.startDate }}</td>
              <td>{{ coaching?.endDate }}</td>
              <td class="text-end">{{ coaching?.unitsDone }}</td>
              <td class="text-end">{{ coaching?.unitsDonePercent }}</td>
              <td class="text-end">
                {{ (Number(coaching.protocolUnits) || 0) + (Number(coaching.firstInterviewUnits) || 0) }}
              </td>
              <td class="text-center">
                <a target="_blank"
                   class="btn portal_pdf_export"
                   @click="downloadFile(`/portal/assisted-living-coaching/meeting-list-pdf/${coaching.id}`,
               undefined, filters)">
                  <img src="../../../../../assets/img/pdf_icon_version4.png" alt="" style="width: 45px; height: 55px;"/>
                  <br>
                </a>
                <a target="_blank"
                   class="btn portal_pdf_export"
                   @click="downloadFile(`/portal/assisted-living-coaching/meeting-list-excel/${coaching.id}`,
               undefined, filters, false )">
                  <img src="../../../../../assets/img/excel_icon.jpg" alt="" style="width: 45px; height: 55px;"/>
                  <br>
                </a>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <Pagination
            v-if="coachingList?.pagination?.total_pages ?? 0 > 1"
            :totalPages="coachingList?.pagination?.total_pages"
            :currentPage="coachingList?.pagination?.current_page"
            :totalItems="coachingList?.pagination?.total_items"
            :perPage="coachingList?.pagination?.items_per_page"
            @pagechanged="getCoachingList"/>
      </div>
    </div>
  </PageTemplate>
</template>

<script setup lang="ts">
import {computed, onMounted, ref} from 'vue';
import analyticsCoachService from '@/api/Analytics/MontlyList';
import Loader from '@/components/Loader.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import Pagination from '@/components/Pagination.vue';
import PerPage from '@/components/PerPage.vue';
import {CloseToast, ErrorToast} from '@/components/VueToast';
// @ts-ignore
import {getAllUnemployedDropdown, getCoachDropdownForAnalytics, getOfficeByUserRoleDropdown, assistedLivingCoachingClassificationDropdown} from '@/shared/dropdowns';
import {piniaEnumsStore} from '@/store/piniaEnumsStore';
import Search from '@/views/App/Analytics/MontlyList/AssistedLivingCoaching/Search.vue';
import {downloadFile} from "@/shared/functions";
import BackButton from "@/components/button/BackButton.vue";

// ---------- Types ----------

interface ICoachingList{
  data: ICoachingData[],
  pagination: IPagination
}

interface ICoachingData{
  id: number;
  status: string;
  firstName: string;
  lastName: string;
  startDate: string;
  endDate: string;
  location: string;
  unitsDone: string;
  unitsDonePercent: string;
  protocolUnits?: number;
  firstInterviewUnits?: number;
}

interface IPagination {
  total_pages: number;
  current_page: number;
  items_per_page?: number;
}

interface IFilters {
  coachId: string;
  lastName: string;
  'officeIds[]': number[];
  clientId: string;
  status: string;
  startDate: string;
  endDate: string;
  page: number,
  limit: number,
}


// ---------- State ----------

const piniaEnumsState = piniaEnumsStore();
const unemployedStatusFiltered = computed(() =>
    piniaEnumsState.enums.unemployedStatus.filter(
        (status: any) => ['active', 'finished', 'new'].includes(status.value)
    )
);

const currentDate = new Date().getFullYear();
const classificationDropdown = assistedLivingCoachingClassificationDropdown();

const filters = ref<IFilters>({
  coachId: '',
  lastName: '',
  'officeIds[]': [],
  clientId: '',
  status: '',
  startDate: `01.01.${currentDate}`,
  endDate: `31.12.${currentDate}`,
  page: 1,
  limit: 10,
});

const coachingList = ref<ICoachingList>();
const coachDropdown = ref<any[]>([]);
const officeDropdown = ref<any[]>([]);
const unemployedDropdown = ref<any[]>([]);
const statusDropdown = ref<any[]>([]);

const isSearching = ref(false);
const isLoaded = ref(false);


// ---------- Methods ----------
async function getCoachingList(
    page = 1,
    perPage?: number,
    passedFilters?: IFilters
) {
  CloseToast();

  if (passedFilters) {
    isSearching.value = true;
  }

  const perPageRes = coachingList.value?.pagination?.items_per_page;

  try {
    const {data} = await analyticsCoachService.getAssistedLivingCoachingList({
      ...(passedFilters ? passedFilters : filters.value),
      page: page,
      limit: perPage ?? perPageRes!,
    });

    console.log("DATA", data)
    coachingList.value = data;
    isSearching.value = false;
  } catch (error: any) {
    await ErrorToast(error);
    isSearching.value = false;
  }
}

const handleDropdownSearch = async (searchTerm: string , type: string) => {
  if (searchTerm.length < 3) return;

  const filters = {
    searchTerm: searchTerm,
    searchTermFor: type
  }

  if (type === 'lastName') await getAllUnemployedDropdown(
      unemployedDropdown,
      'lastName',
      'lastName',
      filters
  )
};

const handleClearDropdown = () => {
  unemployedDropdown.value = [];
}


// ---------- Lifecycle ----------
onMounted(async () => {
  await Promise.all([
    getCoachingList(),
    getOfficeByUserRoleDropdown(officeDropdown),
    getCoachDropdownForAnalytics(coachDropdown),
  ]);

  isLoaded.value = true;
});
</script>

<style scoped>

</style>